/* ============================================================
 * FILE GENERATO — NON MODIFICARE A MANO
 *
 * Sorgente: backend/utils/denaro.js
 * Lo riscrive frontend/scripts/generaDenaro.js a ogni build.
 * Qualunque modifica fatta qui sparisce al build successivo:
 * si corregge l'originale nel backend.
 *
 * Esiste perche' Create React App non compila fuori da src/, e il
 * calcolo di un totale sullo schermo deve dare lo stesso centesimo
 * di quello che finisce in fattura.
 * ============================================================ */

/**
 * denaro.js — aritmetica degli importi, in centesimi interi.
 *
 * PERCHE' ESISTE
 * Il codice calcolava l'IVA con `+(imponibile * perc / 100).toFixed(2)`.
 * I numeri decimali in JavaScript sono binari: un risultato che vale
 * esattamente x,xx5 viene rappresentato come x,xx4999... e toFixed
 * arrotonda per difetto. Verificato su 100.000 importi: succede al 4,8%
 * di quelli con IVA al 10%, al 2,4% con il 5%, allo 0,96% con il 22% - e
 * sempre in DIFETTO, mai in eccesso. Esempi reali: 50,05 al 10% dava 5,00
 * invece di 5,01; 50,25 al 22% dava 11,05 invece di 11,06.
 *
 * Un centesimo per riga non rompe niente e non si vede: e' esattamente il
 * genere di errore che resta per anni, e intanto l'IVA versata e' piu'
 * bassa del dovuto.
 *
 * COME
 * Si passa subito a centesimi interi e si arrotonda mezzo verso l'alto,
 * come vuole la prassi contabile. Postgres fa lo stesso sui `numeric`,
 * quindi i conti fatti qui e quelli fatti dal database coincidono.
 */

/**
 * Da euro a centesimi interi. Gli importi in arrivo hanno 2 decimali.
 *
 * LA VIRGOLA SI NORMALIZZA QUI, E IL MOTIVO E' IL 12 SETTEMBRE 2026
 * Prima questa funzione faceva `parseFloat(valore)` e basta. `parseFloat`
 * si ferma al primo carattere che non sa leggere: «85,50» diventava 85,
 * cioe' 8500 centesimi invece di 8550, e «1.234,56» diventava 1,23 -
 * tre ordini di grandezza - senza sollevare niente. Un numero plausibile
 * e sbagliato, che e' il tipo di errore che resta per anni.
 *
 * Il difetto era latente in tutte e cinque le funzioni di questo file,
 * perche' ci passano tutte da qui. In archivio non ha mai prodotto un
 * importo sbagliato: tutte le schermate di denaro usano input
 * `type="number"`, che restituisce il punto decimale o la stringa vuota,
 * mai la virgola. Ma la difesa stava li', nel tipo di un campo, e non in
 * questo file: e' bastato che il registro RX usasse un campo di testo -
 * giustamente, perche' su un registro tenuto a mano si scrive «85,50» -
 * perche' il difetto uscisse al primo importo con i decimali.
 *
 * Adesso la difesa sta dove il dato viene interpretato, non dove viene
 * digitato. Su tutto quello che arriva oggi - numeri, stringhe col punto
 * dal database, vuoti, null - il comportamento e' identico a prima:
 * l'unica differenza e' che ha smesso di troncare.
 *
 * Con la virgola, la virgola e' il decimale e i punti sono le migliaia:
 * «1.234,56». Senza virgola il punto e' il decimale, perche' e' quello
 * che mandano i campi numerici e il database.
 */
export function aNumero(valore) {
    if (valore === null || valore === undefined) return NaN;
    if (typeof valore === 'number') return valore;
    let s = String(valore).trim().replace(/\s/g, '').replace(/€/g, '');
    if (s === '') return NaN;
    s = s.includes(',') ? s.replace(/\./g, '').replace(',', '.') : s;
    const n = parseFloat(s);
    return Number.isFinite(n) ? n : NaN;
}

export function aCentesimi(valore) {
    const n = aNumero(valore);
    if (!Number.isFinite(n)) return 0;
    return Math.round(n * 100);
}

/** Da centesimi interi a euro. */
export function aEuro(centesimi) {
    return Math.round(centesimi) / 100;
}

/**
 * Imponibile di una riga: prezzo x quantita, meno lo sconto.
 * Lo sconto e' un importo in euro, non una percentuale.
 * La quantita' puo' avere decimali (2,5 fiale), quindi il prodotto si
 * arrotonda al centesimo prima di sottrarre.
 */
export function imponibileRiga(prezzoUnitario, quantita, sconto = 0) {
    const q = aNumero(quantita);
    const cent = Math.round(aCentesimi(prezzoUnitario) * (Number.isFinite(q) ? q : 1))
        - aCentesimi(sconto);
    return aEuro(cent);
}

/**
 * IVA su un imponibile, data l'aliquota in percentuale.
 * 50,05 al 10% -> 5,01 (non 5,00): 5005 x 10 / 100 = 500,5 centesimi,
 * che arrotondato mezzo verso l'alto fa 501.
 */
export function ivaSu(imponibile, percentuale) {
    const perc = aNumero(percentuale);
    if (!Number.isFinite(perc) || perc === 0) return 0;
    return aEuro(aCentesimi(imponibile) * perc / 100);
}

/**
 * Importo unitario x quantita, quando l'unitario ha PIU' di due decimali.
 *
 * Serve per il valore di una seduta di pacchetto, che e' generato come
 * round(prezzo_totale / numero_sedute, 4): passare per i centesimi interi
 * butterebbe via il terzo e il quarto decimale prima di moltiplicare, e su
 * un pacchetto da 300,01 su 4 sedute il valore 75,0025 diventerebbe 75,00,
 * perdendo un centesimo ogni due sedute. Si moltiplica in decimillesimi e
 * si arrotonda solo alla fine.
 */
export function perUnita(unitario, quantita, decimali = 4) {
    /* `aNumero` e non `aCentesimi`: qui servono piu' di due decimali, ed e'
       il motivo per cui la normalizzazione sta in una funzione a parte
       invece che dentro `aCentesimi`. Scoperto provando tutte e cinque le
       funzioni dopo la correzione: tre passavano da `aCentesimi` e due no,
       e la correzione da sola ne avrebbe sistemate tre su cinque. */
    const u = aNumero(unitario);
    const q = aNumero(quantita);
    if (!Number.isFinite(u) || !Number.isFinite(q)) return 0;
    const fattore = Math.pow(10, decimali);
    // in decimillesimi -> per la quantita -> a centesimi -> arrotondato
    return aEuro(Math.round(u * fattore) * q / (fattore / 100));
}

/** Somma di importi senza accumulare errore binario. */
export function somma(...importi) {
    return aEuro(importi.reduce((t, x) => t + aCentesimi(x), 0));
}

/**
 * Divisione di un importo per una quantita', con N decimali.
 *
 * Serve al costo unitario di una riga di fattura fornitore, che vale
 * prezzo_totale / quantita' a quattro decimali. Scritta come
 * `+(totale / quantita).toFixed(4)` sbagliava il quarto decimale nel 4% dei
 * casi, sempre in difetto: 4,93 diviso 8 fa 0,61625 e usciva 0,6162.
 *
 * Non basta arrotondare a mano il quoziente. 4,93 in binario e' 4,9299999...,
 * quindi 4,93 x 10000 fa 49299,999... e la meta' esatta e' gia' persa prima
 * della divisione. Si porta tutto a interi - dividendo e divisore sulla
 * stessa scala - e si divide fra interi, dove il resto dice senza ambiguita'
 * da che parte arrotondare.
 */
export function dividi(totale, quantita, decimali = 4) {
    const t = aNumero(totale);
    const q = aNumero(quantita);
    if (!Number.isFinite(t) || !Number.isFinite(q) || q === 0) return null;

    const fattore = Math.pow(10, decimali);
    const n = Math.round(t * 1e4) * fattore;
    const d = Math.round(q * 1e4);

    /* Math.floor sul quoziente in virgola mobile puo' sbagliare di uno
       all'ultimo bit: il resto lo rimette a posto. */
    let intero = Math.floor(n / d);
    let resto = n - intero * d;
    while (resto < 0) { intero -= 1; resto += d; }
    while (resto >= d) { intero += 1; resto -= d; }

    return (2 * resto >= d ? intero + 1 : intero) / fattore;
}

/**
 * La quantita' di una riga: o e' un numero buono, o si rifiuta.
 *
 * COSA FACEVA PRIMA, in tre punti di `fatturazioneController`:
 * `parseFloat(b.quantita || 1)`. Tre modi diversi di sbagliare, tutti in
 * silenzio, tutti dentro un calcolo di soldi:
 *
 *  - **zero diventa uno.** `0 || 1` fa 1: una riga che qualcuno ha
 *    messo a zero apposta viene fatturata come una unita';
 *  - **una parola diventa NaN.** `'abc'` e' vero, quindi passa il `||`,
 *    e `parseFloat` da' NaN: l'imponibile diventa NaN e se ne accorge —
 *    forse — il database, tre query dopo;
 *  - **«3 pezzi» diventa 3.** `parseFloat` si ferma al primo carattere
 *    che non torna, e non lo dice a nessuno.
 *
 * Una formula sbagliata non da' eccezioni, da' numeri plausibili: e' la
 * regola scritta in CLAUDE.md per le formule cliniche, e vale identica
 * per una fattura.
 *
 * ASSENTE E' DIVERSO DA SBAGLIATO. Se il campo non c'e' si usa il
 * predefinito — quasi sempre 1, ed e' il caso normale di chi fattura una
 * prestazione. Se c'e' e non e' un numero maggiore di zero, si solleva.
 *
 * @param {*} valore       quello che e' arrivato nella richiesta
 * @param {number} predefinito  quando non e' arrivato niente
 * @param {string} dove    per il messaggio, es. «quantita'»
 */
export function quantitaRichiesta(valore, predefinito = 1, dove = 'quantita\'') {
    if (valore === undefined || valore === null || valore === '') return predefinito;

    /* `Number` e non `parseFloat`: parseFloat legge il prefisso e butta
       il resto, Number rifiuta tutta la stringa se non e' un numero. */
    const n = typeof valore === 'number' ? valore : Number(String(valore).replace(',', '.'));
    if (!Number.isFinite(n) || n <= 0) {
        const e = new Error(`La ${dove} deve essere un numero maggiore di zero.`);
        e.stato = 400;
        throw e;
    }
    return n;
}

